import { Link } from '@tanstack/react-router';
import { Check, ChevronDown, Lock, MailPlus, Minus, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { ROLE_LABELS, ROLES, type MemberRole, type PermissionSet, type Role } from '@buildbee/contracts';
import { CATALOG_CELLS, expandPattern, PERMISSION_CATALOG, ROLE_DESCRIPTIONS, type CatalogGroup } from '@buildbee/domain';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Form,
  FormActions,
  FormError,
  FormField,
  Input,
  LoadingState,
  Select,
  Status,
  cn,
  segmentedItemClass,
  segmentedListClass,
  useToast,
  useZodForm,
} from '@buildbee/ui';
import { errorMessage, trpc, type RouterOutputs } from '../../../lib/trpc';
import { InviteDialog, roleOptionsFor } from './invite';

/**
 * Roles & permissions: the ladder (who is above whom), the matrix (what each role can do, roles
 * across the top, grouped actions down the side), editing one focused role at a time, the people
 * who hold that role, and per-person exceptions. The API enforces every rule shown here.
 */

type Matrix = RouterOutputs['workspace']['roles']['matrix'];
type RoleColumn = Matrix['roles'][number];
type CellState = RoleColumn['cells'][string];
type Draft = { cells: Record<string, boolean>; ownRecordsOnly?: boolean };

const SHORT_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  pm: 'PM',
  sales: 'Sales',
  field: 'Field',
  sub: 'Sub',
  client: 'Client',
};

function changeCount(draft: Draft | undefined): number {
  if (!draft) return 0;
  return Object.keys(draft.cells).length + (draft.ownRecordsOnly !== undefined ? 1 : 0);
}

export function RolesSettingsPage() {
  const matrix = trpc.workspace.roles.matrix.useQuery();
  const [focus, setFocus] = useState<Role | null>(null);
  const [drafts, setDrafts] = useState<Partial<Record<Role, Draft>>>({});
  const [query, setQuery] = useState('');
  const [resetting, setResetting] = useState(false);
  const utils = trpc.useUtils();
  const toast = useToast();

  const onSaved = (role: Role, data: Matrix, message: string) => {
    utils.workspace.roles.matrix.setData(undefined, data);
    setDrafts((d) => ({ ...d, [role]: undefined }));
    void utils.workspace.roles.mine.invalidate();
    toast.success(message);
  };
  const onFailed = (title: string) => (e: { data?: { code?: string } | null; message: string }) => {
    if (e.data?.code === 'CONFLICT') {
      toast.error('Someone else changed this role', errorMessage(e));
      void matrix.refetch();
    } else toast.error(title, errorMessage(e));
  };
  const save = trpc.workspace.roles.setPolicy.useMutation({
    onSuccess: (data, vars) => onSaved(vars.role, data, `${ROLE_LABELS[vars.role]} permissions saved`),
    onError: onFailed('Could not save permissions'),
  });
  const reset = trpc.workspace.roles.resetPolicy.useMutation({
    onSuccess: (data, vars) => {
      setResetting(false);
      onSaved(vars.role, data, `${ROLE_LABELS[vars.role]} is back to BuildBee's defaults`);
    },
    onError: onFailed('Could not reset the role'),
  });

  if (matrix.isLoading) return <LoadingState />;
  if (matrix.error || !matrix.data) return <ErrorState error={matrix.error} onRetry={() => void matrix.refetch()} />;
  const data = matrix.data;
  const columns = data.roles;
  const editableList = data.viewer.editableRoles as Role[];
  const focused = focus ?? (editableList.includes('pm') || !editableList.length ? 'pm' : editableList[0]!);
  const focusCol = columns.find((c) => c.role === focused)!;
  const draft = drafts[focused];
  const pending = changeCount(draft);

  const setCell = (cell: string, allowed: boolean) => {
    setDrafts((all) => {
      const current = all[focused] ?? { cells: {} };
      const cells = { ...current.cells };
      if (focusCol.cells[cell]?.allowed === allowed) delete cells[cell];
      else cells[cell] = allowed;
      return { ...all, [focused]: { ...current, cells } };
    });
  };
  const setOwnRecords = (value: boolean) => {
    setDrafts((all) => {
      const current = all[focused] ?? { cells: {} };
      const next: Draft = { cells: current.cells };
      if (value !== focusCol.ownRecordsOnly) next.ownRecordsOnly = value;
      return { ...all, [focused]: next };
    });
  };

  return (
    <div className={cn('space-y-8', pending ? 'pb-24' : undefined)}>
      <section className="space-y-3">
        <div>
          <h2 className="text-[17px] font-semibold tracking-[-0.02em]">Roles</h2>
          <p className="text-sm text-muted-foreground">
            Everyone on your team has one role. A higher role can do everything the roles below it can, unless you take it away. Nobody can give access above their own role.
          </p>
        </div>
        <RoleLadder columns={columns} ladder={data.ladder as Role[][]} focus={focused} onFocus={setFocus} />
      </section>

      <section className="space-y-3" aria-labelledby="matrix-title">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="matrix-title" className="text-[17px] font-semibold tracking-[-0.02em]">
              What each role can do
            </h2>
            <p className="text-sm text-muted-foreground">
              {focusCol.editable
                ? `Tick or untick in the ${ROLE_LABELS[focused]} column, then save.`
                : focused === 'owner'
                  ? 'Owners always have full access.'
                  : data.viewer.canManage
                    ? `You can't change ${ROLE_LABELS[focused]}: only roles below your own.`
                    : 'Only owners and admins can change roles.'}
            </p>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input aria-label="Find a permission" placeholder="Find a permission" className="pl-8" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        </div>

        <RolePicker columns={columns} focus={focused} onFocus={setFocus} drafts={drafts} />

        {focusCol.editable && (focused === 'field' || focused === 'sub') ? (
          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
              <Checkbox
                checked={draft?.ownRecordsOnly ?? focusCol.ownRecordsOnly}
                onCheckedChange={(v) => setOwnRecords(v === true)}
                label={`${ROLE_LABELS[focused]} can only edit records they added or are assigned to`}
              />
            </CardContent>
          </Card>
        ) : null}

        <MatrixTable catalog={data.catalog as CatalogGroup[]} columns={columns} focus={focused} onFocus={setFocus} draft={draft} onToggle={setCell} query={query} />

        {focusCol.editable && focusCol.customized ? (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={() => setResetting(true)}>
              Reset {ROLE_LABELS[focused]} to BuildBee's defaults
            </Button>
          </div>
        ) : null}
      </section>

      <PeoplePanel role={focused} assignable={data.viewer.assignableRoles as MemberRole[]} />

      <ExceptionsSection canManage={data.viewer.canManage} />

      {pending ? (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t bg-card/95 backdrop-blur print:hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-8">
            <p className="text-sm">
              <span className="figures font-semibold">{pending}</span> {pending === 1 ? 'change' : 'changes'} to {ROLE_LABELS[focused]}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDrafts((d) => ({ ...d, [focused]: undefined }))}>
                Discard
              </Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  save.mutate({
                    role: focused as Exclude<MemberRole, 'owner'>,
                    changes: Object.entries(draft!.cells).map(([cell, allowed]) => ({ cell, allowed })),
                    ...(draft!.ownRecordsOnly !== undefined ? { ownRecordsOnly: draft!.ownRecordsOnly } : {}),
                    expectedVersion: focusCol.policyVersion,
                  })
                }
              >
                Save {ROLE_LABELS[focused]} permissions
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={resetting}
        onOpenChange={setResetting}
        title={`Reset ${ROLE_LABELS[focused]}?`}
        description={`Every change your company made to ${ROLE_LABELS[focused]} is undone, and people with this role get BuildBee's standard access.`}
        confirmLabel="Reset role"
        loading={reset.isPending}
        onConfirm={() => reset.mutate({ role: focused as Exclude<MemberRole, 'owner'>, expectedVersion: focusCol.policyVersion })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ladder
// ---------------------------------------------------------------------------

function RoleLadder({ columns, ladder, focus, onFocus }: { columns: RoleColumn[]; ladder: Role[][]; focus: Role; onFocus: (r: Role) => void }) {
  const byRole = new Map(columns.map((c) => [c.role, c]));
  return (
    <Card>
      <CardContent className="p-4 sm:p-5">
        <ol aria-label="Role ladder, highest first" className="space-y-0">
          {ladder.map((rung, i) => (
            <li key={rung.join('-')} className="flex gap-3">
              <div className="flex w-5 shrink-0 flex-col items-center pt-3" aria-hidden>
                <span className={cn('size-2.5 rounded-full border-2', rung.includes(focus) ? 'border-primary bg-primary' : 'border-foreground/40 bg-card')} />
                {i < ladder.length - 1 ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
              </div>
              <div className="flex min-w-0 flex-1 flex-wrap gap-2 pb-2">
                {rung.map((role) => {
                  const col = byRole.get(role)!;
                  return (
                    <button
                      key={role}
                      type="button"
                      onClick={() => onFocus(role)}
                      aria-pressed={focus === role}
                      className={cn(
                        'flex min-w-0 flex-1 basis-60 flex-col justify-start rounded-lg border px-3 py-2 text-left transition-colors hover:bg-foreground/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                        focus === role ? 'border-primary bg-primary/[0.06]' : 'border-border',
                      )}
                    >
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-medium">{ROLE_LABELS[role]}</span>
                        {col.memberCount !== null ? (
                          <span className="text-xs text-muted-foreground">
                            <span className="figures">{col.memberCount}</span> {col.memberCount === 1 ? 'person' : 'people'}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">Client portal only</span>
                        )}
                        {col.customized ? <Status tone="progress">Changed for your company</Status> : null}
                      </span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[role]}</span>
                      {col.inheritsFrom.length ? (
                        <span className="mt-1 block text-xs text-muted-foreground">
                          Includes {col.inheritsFrom.map((r) => ROLE_LABELS[r as Role]).join(' and ')}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Role picker: segmented on wider screens, a select on phones
// ---------------------------------------------------------------------------

function RolePicker({ columns, focus, onFocus, drafts }: { columns: RoleColumn[]; focus: Role; onFocus: (r: Role) => void; drafts: Partial<Record<Role, Draft>> }) {
  return (
    <>
      <div className="sm:hidden">
        <Select
          aria-label="Role to show"
          value={focus}
          onChange={(e) => onFocus(e.target.value as Role)}
          options={columns.map((c) => ({ value: c.role, label: `${ROLE_LABELS[c.role]}${c.editable ? '' : ' (view only)'}` }))}
        />
      </div>
      <div role="tablist" aria-label="Role to focus" className={cn(segmentedListClass, 'hidden sm:inline-flex')}>
        {columns.map((c) => (
          <button key={c.role} type="button" role="tab" aria-selected={focus === c.role} className={segmentedItemClass(focus === c.role)} onClick={() => onFocus(c.role)}>
            {c.editable ? null : <Lock className="size-3 opacity-60" aria-label="view only" />}
            {SHORT_LABELS[c.role]}
            {changeCount(drafts[c.role]) ? <span className="figures text-[11px] text-primary">•{changeCount(drafts[c.role])}</span> : null}
          </button>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Matrix
// ---------------------------------------------------------------------------

function cellTitle(col: RoleColumn, state: CellState | undefined): string {
  if (!state) return '';
  if (state.locked === 'owner_only') return 'Owners only';
  if (col.role === 'owner') return 'Owners always have full access';
  if (state.locked === 'required') return 'Everyone on the team needs this';
  switch (state.source) {
    case 'inherited':
      return `Included from ${ROLE_LABELS[state.from as Role]}`;
    case 'added':
      return 'Added for your company';
    case 'removed':
      return 'Taken away for your company';
    default:
      return state.allowed ? "BuildBee's default" : 'Not included';
  }
}

function TickMark({ col, state }: { col: RoleColumn; state: CellState | undefined }) {
  const title = cellTitle(col, state);
  const label = `${ROLE_LABELS[col.role]}: ${state?.allowed ? 'yes' : 'no'}. ${title}`;
  if (state?.locked === 'owner_only' && !state.allowed) {
    return <Lock className="mx-auto size-3.5 text-muted-foreground/70" aria-label={label} />;
  }
  if (state?.allowed) {
    return (
      <span className="relative inline-flex" role="img" aria-label={label}>
        <Check className={cn('size-4', state.source === 'inherited' ? 'text-muted-foreground' : 'text-foreground')} strokeWidth={state.source === 'inherited' ? 2 : 2.5} />
        {state.source === 'added' ? <span className="absolute -right-1 -top-0.5 size-1.5 rounded-full bg-primary" /> : null}
      </span>
    );
  }
  if (state?.source === 'removed') return <Minus className="mx-auto size-4 text-muted-foreground" aria-label={label} />;
  return <span className="sr-only">{label}</span>;
}

function MatrixTable({
  catalog,
  columns,
  focus,
  onFocus,
  draft,
  onToggle,
  query,
}: {
  catalog: CatalogGroup[];
  columns: RoleColumn[];
  focus: Role;
  onFocus: (r: Role) => void;
  draft: Draft | undefined;
  onToggle: (cell: string, allowed: boolean) => void;
  query: string;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const q = query.trim().toLowerCase();
  const focusCol = columns.find((c) => c.role === focus)!;
  const groups = useMemo(
    () =>
      catalog
        .map((g) => ({
          ...g,
          cells: g.items.flatMap((i) => i.cells).filter((c) => !q || c.label.toLowerCase().includes(q) || g.label.toLowerCase().includes(q)),
        }))
        .filter((g) => g.cells.length),
    [catalog, q],
  );
  if (!groups.length) return <EmptyState title="No permissions match" description="Try another word, like invoices or photos." />;

  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">Permissions by role. The {ROLE_LABELS[focus]} column is in focus.</caption>
        <thead className="sticky top-0 z-10 bg-card">
          <tr className="border-b">
            <th scope="col" className="sticky left-0 z-10 min-w-[13rem] bg-card px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              Can…
            </th>
            {columns.map((c) => (
              <th key={c.role} scope="col" className={cn('px-1 py-2 text-center', c.role === focus ? 'bg-primary/[0.06] table-cell' : 'hidden sm:table-cell')}>
                <button type="button" onClick={() => onFocus(c.role)} className="inline-flex flex-col items-center rounded-md px-2 py-0.5 text-xs font-semibold hover:bg-foreground/[0.04]">
                  {SHORT_LABELS[c.role]}
                  {c.editable ? null : <span className="text-[10px] font-normal text-muted-foreground">view</span>}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        {groups.map((g) => {
          const open = !collapsed[g.id] || !!q;
          const ticked = g.cells.filter((c) => {
            const key = `${c.resource}:${c.action}`;
            return draft?.cells[key] ?? focusCol.cells[key]?.allowed;
          }).length;
          return (
            <tbody key={g.id}>
              <tr className="border-b bg-foreground/[0.03]">
                <th scope="colgroup" colSpan={columns.length + 1} className="px-3 py-1.5 text-left">
                  <button type="button" className="flex w-full items-center gap-2 text-xs font-semibold" aria-expanded={open} onClick={() => setCollapsed((s) => ({ ...s, [g.id]: open }))}>
                    <ChevronDown className={cn('size-3.5 transition-transform', open ? '' : '-rotate-90')} aria-hidden />
                    {g.label}
                    <span className="figures font-normal text-muted-foreground">
                      {ROLE_LABELS[focus]}: {ticked} of {g.cells.length}
                    </span>
                  </button>
                </th>
              </tr>
              {open
                ? g.cells.map((cell) => {
                    const key = `${cell.resource}:${cell.action}`;
                    return (
                      <tr key={key} className="border-b last:border-b-0 hover:bg-foreground/[0.02]">
                        <th scope="row" className="sticky left-0 bg-card px-3 py-1.5 text-left font-normal">
                          {cell.label}
                        </th>
                        {columns.map((c) => {
                          const state = c.cells[key];
                          const isFocus = c.role === focus;
                          const editable = isFocus && c.editable && !state?.locked;
                          const changed = isFocus && draft?.cells[key] !== undefined;
                          return (
                            <td
                              key={c.role}
                              title={cellTitle(c, state)}
                              className={cn('px-1 py-1.5 text-center', isFocus ? 'table-cell bg-primary/[0.06]' : 'hidden sm:table-cell', changed && 'bg-primary/[0.14]')}
                            >
                              {editable ? (
                                <Checkbox
                                  aria-label={`${ROLE_LABELS[c.role]}: ${cell.label}`}
                                  checked={draft?.cells[key] ?? state?.allowed ?? false}
                                  onCheckedChange={(v) => onToggle(key, v === true)}
                                />
                              ) : (
                                <TickMark col={c} state={state} />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                : null}
            </tbody>
          );
        })}
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// People with the focused role
// ---------------------------------------------------------------------------

type MemberRow = RouterOutputs['workspace']['members']['list'][number];

function PeoplePanel({ role, assignable }: { role: Role; assignable: MemberRole[] }) {
  const members = trpc.workspace.members.list.useQuery({ includeSuspended: true });
  const current = trpc.workspace.current.useQuery();
  const utils = trpc.useUtils();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const [changing, setChanging] = useState<{ member: MemberRow; role: MemberRole } | null>(null);
  const update = trpc.workspace.members.update.useMutation({
    onSuccess: async (_d, vars) => {
      toast.success('Role changed', vars.role ? `Now ${ROLE_LABELS[vars.role]}.` : undefined);
      setChanging(null);
      await Promise.all([utils.workspace.members.list.invalidate(), utils.workspace.roles.matrix.invalidate()]);
    },
    onError: (e) => toast.error('Could not change the role', errorMessage(e)),
  });

  if (role === 'client') {
    return (
      <section className="space-y-2">
        <h2 className="text-[17px] font-semibold tracking-[-0.02em]">Clients</h2>
        <p className="text-sm text-muted-foreground">
          Clients are not team members. Invite them and choose what they see from{' '}
          <Link to="/portal/admin" className="font-medium text-foreground underline underline-offset-2">
            Client portal
          </Link>
          .
        </p>
      </section>
    );
  }
  const me = current.data?.membership.id;
  const people = (members.data ?? []).filter((m) => m.role === role);
  const canInvite = assignable.some((r) => r !== 'owner');

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[17px] font-semibold tracking-[-0.02em]">
          People who are {ROLE_LABELS[role]} <span className="figures text-muted-foreground">({people.length})</span>
        </h2>
        {canInvite && role !== 'owner' && assignable.includes(role as MemberRole) ? (
          <Button variant="outline" onClick={() => setInviting(true)}>
            <MailPlus /> Invite a {ROLE_LABELS[role]}
          </Button>
        ) : null}
      </div>
      {members.isLoading ? (
        <LoadingState />
      ) : members.error ? (
        <ErrorState error={members.error} onRetry={() => void members.refetch()} />
      ) : people.length === 0 ? (
        <EmptyState title={`Nobody is ${ROLE_LABELS[role]} yet`} description="Change someone's role below their name, or invite a new person." />
      ) : (
        <Card>
          <ul className="divide-y">
            {people.map((m) => {
              const editable = m.id !== me && assignable.includes(m.role as MemberRole);
              return (
                <li key={m.id} className="flex flex-wrap items-center gap-3 p-3">
                  <Avatar name={m.name} src={m.image} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {m.name} {m.id === me ? <span className="text-xs text-muted-foreground">(you)</span> : null}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                  </div>
                  {m.status !== 'active' ? <Status tone="blocked">Suspended</Status> : null}
                  {editable ? (
                    <Select
                      aria-label={`Role for ${m.name}`}
                      className="h-8 w-48"
                      value={m.role}
                      options={roleOptionsFor(assignable)}
                      onChange={(e) => {
                        const next = e.target.value as MemberRole;
                        if (next !== m.role) setChanging({ member: m, role: next });
                      }}
                    />
                  ) : (
                    <Badge>{ROLE_LABELS[m.role]}</Badge>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>
      )}
      <InviteDialog open={inviting} onOpenChange={setInviting} roles={assignable} defaultRole={role as MemberRole} onDone={() => utils.workspace.invitations.list.invalidate()} />
      <ConfirmDialog
        open={!!changing}
        onOpenChange={(o) => !o && setChanging(null)}
        title={changing ? `Make ${changing.member.name} ${ROLE_LABELS[changing.role]}?` : 'Change role?'}
        description={changing ? `${ROLE_DESCRIPTIONS[changing.role]} The change takes effect right away.` : undefined}
        confirmLabel="Change role"
        loading={update.isPending}
        onConfirm={() => changing && update.mutate({ id: changing.member.id, role: changing.role })}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Per-person exceptions (permission templates)
// ---------------------------------------------------------------------------

type TemplateRow = RouterOutputs['workspace']['roles']['templates']['list'][number];

function ExceptionsSection({ canManage }: { canManage: boolean }) {
  const templates = trpc.workspace.roles.templates.list.useQuery();
  const utils = trpc.useUtils();
  const toast = useToast();
  const [editing, setEditing] = useState<TemplateRow | 'new' | null>(null);
  const archive = trpc.workspace.roles.templates.archive.useMutation({
    onSuccess: async () => {
      toast.success('Exception removed');
      await utils.workspace.roles.templates.list.invalidate();
    },
    onError: (e) => toast.error('Could not remove it', errorMessage(e)),
  });
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[17px] font-semibold tracking-[-0.02em]">Exceptions for individual people</h2>
          <p className="text-sm text-muted-foreground">
            A set of extra or fewer permissions you can give one person on the Members page, or on one project from the project's Team page. Project settings win on that project.
          </p>
        </div>
        {canManage ? (
          <Button variant="outline" onClick={() => setEditing('new')}>
            <Plus /> New exception
          </Button>
        ) : null}
      </div>
      {templates.isLoading ? (
        <LoadingState />
      ) : templates.error ? (
        <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
      ) : !templates.data?.length ? (
        <EmptyState title="No exceptions yet" description="Most teams never need one: change the role instead." />
      ) : (
        <Card>
          <ul className="divide-y">
            {templates.data.map((t) => (
              <li key={t.id} className="flex flex-wrap items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">
                    {t.name} {t.isSystem ? <Badge variant="outline">Starter</Badge> : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    For {ROLE_LABELS[t.baseRole]}
                    {t.description ? ` · ${t.description}` : ''}
                  </p>
                </div>
                {canManage ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setEditing(t)}>
                      Edit
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => archive.mutate({ id: t.id })}>
                      Remove
                    </Button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {editing ? <ExceptionDialog template={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

type Choice = 'role' | 'allow' | 'deny';

/** Catalog cell choices from stored patterns (wildcards expanded). */
export function choicesFromPermissions(p: PermissionSet | null | undefined): Record<string, Choice> {
  const allow = new Set((p?.allow ?? []).flatMap(expandPattern));
  const deny = new Set((p?.deny ?? []).flatMap(expandPattern));
  const out: Record<string, Choice> = {};
  for (const c of CATALOG_CELLS) {
    const key = `${c.resource}:${c.action}`;
    out[key] = deny.has(key as never) ? 'deny' : allow.has(key as never) ? 'allow' : 'role';
  }
  return out;
}

/** Stored patterns from choices; keeps the original patterns when nothing on the page changed. */
export function permissionsFromChoices(original: PermissionSet | null | undefined, choices: Record<string, Choice>): PermissionSet {
  const before = choicesFromPermissions(original);
  const unchanged = Object.keys(choices).every((k) => choices[k] === before[k]);
  if (original && unchanged) return original;
  const allow = Object.keys(choices).filter((k) => choices[k] === 'allow');
  const deny = Object.keys(choices).filter((k) => choices[k] === 'deny');
  return { allow, deny, ...(original?.ownRecordsOnly !== undefined ? { ownRecordsOnly: original.ownRecordsOnly } : {}) };
}

const exceptionForm = z.object({ name: z.string().trim().min(1, 'Give it a name').max(120), description: z.string().max(1000).optional(), baseRole: z.string() });

function ExceptionDialog({ template, onClose }: { template: TemplateRow | null; onClose: () => void }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const [choices, setChoices] = useState(() => choicesFromPermissions(template?.permissions));
  const done = async () => {
    toast.success(template ? 'Exception saved' : 'Exception created');
    await utils.workspace.roles.templates.list.invalidate();
    onClose();
  };
  const create = trpc.workspace.roles.templates.create.useMutation({ onSuccess: done });
  const update = trpc.workspace.roles.templates.update.useMutation({ onSuccess: done });
  const form = useZodForm(exceptionForm, {
    defaultValues: { name: template?.name ?? '', description: template?.description ?? '', baseRole: template?.baseRole ?? 'pm' },
  });
  const error = create.error ?? update.error;
  const changed = Object.values(choices).filter((c) => c !== 'role').length;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg" title={template ? 'Edit exception' : 'New exception'} description="Pick what this person can also do, or can't do, on top of their role.">
        <Form
          form={form}
          onSubmit={async (v) => {
            const body = { name: v.name, description: v.description, baseRole: v.baseRole as Role, permissions: permissionsFromChoices(template?.permissions, choices) };
            if (template) await update.mutateAsync({ id: template.id, expectedVersion: template.version, ...body });
            else await create.mutateAsync(body);
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField name="name" label="Name" required>
              <Input placeholder="e.g. Foreman who approves daily logs" />
            </FormField>
            <FormField name="baseRole" label="For people who are" required>
              <Select options={ROLES.filter((r) => r !== 'owner' && r !== 'client').map((r) => ({ value: r, label: ROLE_LABELS[r] }))} />
            </FormField>
          </div>
          <FormField name="description" label="Note">
            <Input />
          </FormField>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto rounded-lg border p-2">
            <p className="px-1 text-xs text-muted-foreground">
              <span className="figures">{changed}</span> changed. "Role" keeps what their role allows.
            </p>
            {PERMISSION_CATALOG.map((g) => (
              <details key={g.id} className="rounded-md">
                <summary className="cursor-pointer rounded-md px-2 py-1.5 text-sm font-semibold hover:bg-foreground/[0.03]">{g.label}</summary>
                <ul className="divide-y">
                  {g.items.flatMap((i) => i.cells).map((c) => {
                    const key = `${c.resource}:${c.action}`;
                    return (
                      <li key={key} className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5">
                        <span className="text-sm">{c.label}</span>
                        <div role="radiogroup" aria-label={c.label} className={segmentedListClass}>
                          {(['role', 'allow', 'deny'] as const).map((choice) => (
                            <button
                              key={choice}
                              type="button"
                              role="radio"
                              aria-checked={choices[key] === choice}
                              className={cn(segmentedItemClass(choices[key] === choice), 'h-7 px-2.5 text-xs')}
                              onClick={() => setChoices((s) => ({ ...s, [key]: choice }))}
                            >
                              {choice === 'role' ? 'Role' : choice === 'allow' ? 'Also allow' : 'Never'}
                            </button>
                          ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </details>
            ))}
          </div>
          <FormError message={error ? errorMessage(error) : null} />
          <FormActions>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending || update.isPending}>
              Save exception
            </Button>
          </FormActions>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
